import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Crown, Check, ArrowLeft, Lock, Zap, MessageSquare, Bell, CheckCircle2, Gift, RefreshCw, Smartphone, Star } from "lucide-react";
import { toast } from "sonner";
import { isIapAvailable, purchaseTier, restorePurchases, getTierPackage } from "@/lib/iap";
import { isNative, platform } from "@/lib/native";
import { TIERS, TRIAL_DAYS } from "@/lib/tiers";

const FEATURES_FREE = [
  "1 active job at a time",
  "Submit 1 active quote",
  "Message owners on jobs you've quoted",
  "Standard profile listing",
];

const FEATURES_PRO = [
  { icon: Lock, text: "Unlock 3 concurrent jobs in your radius" },
  { icon: Zap, text: "Submit up to 3 active quotes" },
  { icon: MessageSquare, text: "Message owners on any unlocked job" },
  { icon: Star, text: "Pro badge on your profile" },
];

const FEATURES_PREMIUM = [
  { icon: Lock, text: "Unlock every job in your radius — no cap" },
  { icon: Zap, text: "Submit unlimited concurrent quotes" },
  { icon: MessageSquare, text: "Message any owner before quoting" },
  { icon: Bell, text: "Priority placement in owner search results" },
  { icon: Crown, text: "Premium badge on your profile" },
];

export default function Upgrade() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [subState, setSubState] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [subscribeLoading, setSubscribeLoading] = useState(null); // tier id ('pro'|'premium') while loading
  const [jobCount, setJobCount] = useState(null);
  // Native IAP state — { pro: pkg, premium: pkg }
  const [iapAvailable, setIapAvailable] = useState(false);
  const [iapPackages, setIapPackages] = useState({ pro: null, premium: null });
  const [restoring, setRestoring] = useState(false);
  const currentTier = user?.subscription_tier || "free";
  const isPaid = currentTier === "pro" || currentTier === "premium";
  const trialUntil = user?.welcome_trial_until;
  const premiumUntil = user?.premium_until;
  const isOnTrial = !!(isPaid && trialUntil && premiumUntil && new Date(premiumUntil).getTime() <= new Date(trialUntil).getTime() + 60_000);
  const trialDaysLeft = isOnTrial && trialUntil ? Math.max(0, Math.ceil((new Date(trialUntil).getTime() - Date.now()) / 86_400_000)) : 0;
  const trialEligible = !!user?.trial_eligible;
  const subscriptionParam = params.get("subscription");
  const cancelled = params.get("subscription") === "cancelled" || params.get("cancelled");

  const refreshSubState = useCallback(() => {
    return api.get("/billing/subscription").then((res) => setSubState(res.data)).catch(() => setSubState(null));
  }, []);
  useEffect(() => { refreshSubState(); }, [refreshSubState]);

  useEffect(() => {
    if (user?.role !== "shop") return;
    api.get("/shop/nearby-job-count").then((r) => setJobCount(r.data)).catch(() => setJobCount(null));
  }, [user]);

  // Native IAP probe — fires only on iOS/Android with a RevenueCat key configured.
  useEffect(() => {
    let cancelledLocal = false;
    (async () => {
      const avail = isIapAvailable();
      if (!avail) { setIapAvailable(false); return; }
      const proPkg = await getTierPackage("pro");
      const premiumPkg = await getTierPackage("premium");
      if (!cancelledLocal) {
        setIapAvailable(!!(proPkg || premiumPkg));
        setIapPackages({ pro: proPkg, premium: premiumPkg });
      }
    })();
    return () => { cancelledLocal = true; };
  }, []);

  const onSubscribeNative = async (tier) => {
    setSubscribeLoading(tier);
    try {
      const r = await purchaseTier(tier);
      if (r.ok) {
        toast.success(`Welcome to ${tier === "premium" ? "Premium" : "Pro"} 🎉`);
        await checkAuth();
        await refreshSubState();
      } else if (r.userCancelled) {
        // user dismissed the native sheet — no toast needed
      } else {
        toast.error(r.error || "Purchase failed");
      }
    } finally {
      setSubscribeLoading(null);
    }
  };

  const onRestore = async () => {
    setRestoring(true);
    try {
      const r = await restorePurchases();
      if (r.ok && r.restored) {
        toast.success("Subscription restored — welcome back");
        await checkAuth();
        await refreshSubState();
      } else if (r.ok) {
        toast.info("No previous subscription found for this account.");
      } else {
        toast.error(r.error || "Restore failed");
      }
    } finally {
      setRestoring(false);
    }
  };

  const onSubscribeRecurring = async (tier, claimTrial = false) => {
    setSubscribeLoading(tier);
    try {
      const res = await api.post("/billing/subscription/checkout", {
        origin_url: window.location.origin,
        tier,
        claim_trial: !!claimTrial,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("No checkout URL returned");
        setSubscribeLoading(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start subscription checkout");
      setSubscribeLoading(null);
    }
  };

  const onOpenPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await api.post("/billing/portal", { origin_url: window.location.origin });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Portal unavailable");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open billing portal");
    } finally {
      setPortalLoading(false);
    }
  };

  useEffect(() => {
    if (cancelled) {
      toast.info("Checkout cancelled. No charge was made.");
      setParams({}, { replace: true });
      return;
    }
    if (subscriptionParam === "success") {
      toast.success("Subscription active 🎉 — give us a few seconds while Stripe finishes…");
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        await Promise.all([checkAuth(), refreshSubState()]);
        if (attempts >= 6) {
          setParams({}, { replace: true });
          return;
        }
        setTimeout(poll, 1500);
      };
      poll();
    }
  }, [subscriptionParam, cancelled, checkAuth, refreshSubState, setParams]);

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      <main className="max-w-[1200px] mx-auto px-4 sm:px-8 lg:px-12 py-12" data-testid="upgrade-page">
        <button onClick={() => navigate(-1)} className="text-zinc-500 hover:text-white text-sm flex items-center gap-1 mb-8" data-testid="upgrade-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="mb-12">
          <div className="label-eyebrow text-[#FF3B30] mb-3 flex items-center gap-2"><Crown className="w-3 h-3" /> MECHANICAR PLANS</div>
          <h1 className="font-heading text-5xl sm:text-6xl font-black tracking-tighter mb-4">More jobs.<br /><span className="text-[#FF3B30]">More revenue.</span></h1>
          <p className="text-zinc-400 text-lg max-w-2xl">Free shops see one job at a time. Pro unlocks three. Premium unlocks every nearby job — no cap.</p>
          {isPaid && user?.premium_until && (
            <div className={`mt-4 inline-flex items-center gap-2 border text-xs uppercase tracking-widest px-3 py-2 ${isOnTrial ? "border-[#FF9500]/40 bg-[#FF9500]/5 text-[#FF9500]" : "border-[#34C759]/40 bg-[#34C759]/5 text-[#34C759]"}`} data-testid="paid-active-banner">
              {isOnTrial ? (
                <><Gift className="w-3 h-3" /> Free trial · {trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"} left · ends {new Date(user.premium_until).toLocaleDateString()}</>
              ) : (
                <><CheckCircle2 className="w-3 h-3" /> {currentTier === "premium" ? "Premium" : "Pro"} active until {new Date(user.premium_until).toLocaleDateString()}</>
              )}
            </div>
          )}
        </div>

        {/* Active trial callout */}
        {isOnTrial && (
          <div className="border border-[#FF9500]/40 bg-gradient-to-r from-[#FF9500]/15 to-[#FF9500]/0 px-5 py-4 mb-6 flex flex-wrap items-center gap-3" data-testid="welcome-trial-banner">
            <Gift className="w-5 h-5 text-[#FF9500] shrink-0" />
            <div className="flex-1 text-sm">
              <span className="text-white font-semibold">You're on your free {TRIAL_DAYS}-day trial.</span>
              <span className="text-zinc-200"> Every nearby job is unlocked. You'll be charged ${currentTier === "premium" ? TIERS.premium.priceLabel.replace("$", "") : TIERS.pro.priceLabel.replace("$", "")} when the trial ends — cancel any time before then.</span>
            </div>
          </div>
        )}

        {/* Trial-eligible callout */}
        {trialEligible && !isPaid && (
          <div className="border border-[#FF9500]/40 bg-gradient-to-r from-[#FF9500]/15 to-[#FF9500]/0 px-5 py-4 mb-6 flex flex-wrap items-center gap-3" data-testid="trial-eligible-banner">
            <Gift className="w-5 h-5 text-[#FF9500] shrink-0" />
            <div className="flex-1 text-sm">
              <span className="text-white font-semibold">{TRIAL_DAYS}-day free trial available.</span>
              <span className="text-zinc-300"> Pick Pro or Premium below — add a payment method, no charge for {TRIAL_DAYS} days, cancel anytime before then.</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {/* FREE */}
          <div className="bg-[#121214] p-8">
            <div className="flex items-baseline justify-between mb-1">
              <h3 className="font-heading text-2xl font-bold">Free</h3>
              {currentTier === "free" && <div className="label-eyebrow text-zinc-500">CURRENT</div>}
            </div>
            <div className="font-heading text-5xl font-black tracking-tighter mb-6">$0<span className="text-zinc-500 text-base font-normal ml-1">/month</span></div>
            <ul className="space-y-3 mb-8">
              {FEATURES_FREE.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                  <Check className="w-4 h-4 text-zinc-600 mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            {jobCount && jobCount.total > 0 && jobCount.tier === "free" ? (
              <div className="border border-[#FF3B30]/30 bg-[#FF3B30]/5 px-3 py-2.5 mt-2 text-xs text-zinc-200 font-mono" data-testid="free-card-job-counter">
                You're seeing <span className="text-white font-bold">{jobCount.visible}</span> of <span className="text-[#FF3B30] font-bold">{jobCount.total}</span> nearby job{jobCount.total === 1 ? "" : "s"} right now.
                {jobCount.total > 1 && <span className="text-zinc-500"> {jobCount.total - jobCount.visible} hidden →</span>}
              </div>
            ) : (
              <div className="h-10" />
            )}
          </div>

          {/* PRO */}
          <TierCard
            tier="pro"
            title="Pro"
            color="#FF9500"
            icon={Star}
            price={TIERS.pro.priceLabel}
            badge={currentTier === "pro" ? "Active" : null}
            features={FEATURES_PRO}
            currentTier={currentTier}
            isOnTrial={isOnTrial}
            trialEligible={trialEligible}
            subState={subState}
            iapAvailable={iapAvailable}
            iapPackage={iapPackages.pro}
            subscribeLoading={subscribeLoading}
            onSubscribeNative={() => onSubscribeNative("pro")}
            onSubscribeRecurring={(claimTrial) => onSubscribeRecurring("pro", claimTrial)}
            onOpenPortal={onOpenPortal}
            portalLoading={portalLoading}
            onRestore={onRestore}
            restoring={restoring}
          />

          {/* PREMIUM */}
          <TierCard
            tier="premium"
            title="Premium"
            color="#FF3B30"
            icon={Crown}
            price={TIERS.premium.priceLabel}
            badge={currentTier === "premium" ? "Active" : "Recommended"}
            features={FEATURES_PREMIUM}
            currentTier={currentTier}
            isOnTrial={isOnTrial}
            trialEligible={trialEligible}
            subState={subState}
            iapAvailable={iapAvailable}
            iapPackage={iapPackages.premium}
            subscribeLoading={subscribeLoading}
            onSubscribeNative={() => onSubscribeNative("premium")}
            onSubscribeRecurring={(claimTrial) => onSubscribeRecurring("premium", claimTrial)}
            onOpenPortal={onOpenPortal}
            portalLoading={portalLoading}
            onRestore={onRestore}
            restoring={restoring}
            highlighted
          />
        </div>

        {/* Native restore link */}
        {iapAvailable && (
          <button
            onClick={onRestore}
            disabled={restoring}
            className="btn-secondary mt-6 mx-auto flex items-center justify-center gap-2"
            data-testid="restore-purchases-btn"
          >
            <RefreshCw className={`w-4 h-4 ${restoring ? "animate-spin" : ""}`} />
            {restoring ? "Restoring…" : "Restore purchases"}
          </button>
        )}

        <div className="mt-10 text-xs text-zinc-500 text-center max-w-xl mx-auto">
          Secure checkout & billing by Stripe on web, by {platform() === "ios" ? "Apple" : "Google Play"} on mobile. Cancel anytime — you keep access until the end of your billing period.
        </div>
      </main>
    </div>
  );
}

/** A single subscription tier card. */
function TierCard({
  tier, title, color, icon: Icon, price, badge, features, currentTier, isOnTrial,
  trialEligible, subState, iapAvailable, iapPackage, subscribeLoading,
  onSubscribeNative, onSubscribeRecurring, onOpenPortal, portalLoading, highlighted,
}) {
  const isCurrent = currentTier === tier;
  const subActive = !!subState?.stripe_subscription_id && subState?.tier === tier;
  const loading = subscribeLoading === tier;
  const fineprint = trialEligible
    ? `Card required · No charge for ${TRIAL_DAYS} days · Auto-renews at ${price}/mo · Cancel anytime`
    : null;

  const ctaLabel = (() => {
    if (subActive) return "Subscription active";
    if (isCurrent) return "Current plan";
    if (trialEligible) return `Start ${TRIAL_DAYS}-day free trial`;
    return `Subscribe — ${price}/month`;
  })();

  return (
    <div
      className={`bg-[#121214] p-8 relative ${highlighted ? `ring-2 -m-px` : ""}`}
      style={highlighted ? { "--tw-ring-color": `${color}66` } : {}}
      data-testid={`tier-card-${tier}`}
    >
      {badge && (
        <div className="absolute top-0 right-0 text-white text-[10px] uppercase tracking-widest px-3 py-1" style={{ background: color }}>
          {badge}
        </div>
      )}
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="font-heading text-2xl font-bold flex items-center gap-2">
          <Icon className="w-5 h-5" style={{ color }} /> {title}
        </h3>
      </div>
      <div className="font-heading text-5xl font-black tracking-tighter mb-1">{price}<span className="text-zinc-500 text-base font-normal ml-1">/month</span></div>
      <div className="text-[11px] text-zinc-500 mb-6">Auto-renews monthly. Cancel anytime — no commitment.</div>
      <ul className="space-y-3 mb-6">
        {features.map((f) => {
          const FIcon = f.icon;
          return (
            <li key={f.text} className="flex items-start gap-2 text-sm text-zinc-100">
              <FIcon className="w-4 h-4 mt-0.5 shrink-0" style={{ color }} />
              <span>{f.text}</span>
            </li>
          );
        })}
      </ul>

      {!subActive && !isCurrent && (
        <div className="flex items-center justify-center gap-1.5 text-[11px] text-zinc-400 mb-3 font-mono">
          <Check className="w-3 h-3 text-[#34C759]" strokeWidth={3} />
          Cancel anytime — no commitment
        </div>
      )}

      {iapAvailable ? (
        <button
          onClick={onSubscribeNative}
          disabled={loading || !iapPackage || subActive}
          className="btn-primary w-full flex items-center justify-center gap-2"
          data-testid={`subscribe-iap-${tier}-btn`}
          style={highlighted ? { background: color } : {}}
        >
          <Smartphone className={`w-4 h-4 ${loading ? "animate-pulse" : ""}`} />
          {loading
            ? "Opening Store…"
            : subActive
              ? `${title} active`
              : !iapPackage
                ? "Not available"
                : `Subscribe — ${iapPackage?.product?.priceString || price + "/mo"}`}
        </button>
      ) : isNative() ? (
        // Google Play policy requires in-app digital subscriptions go through
        // Play Billing — Stripe checkout is NOT allowed on Android. Until our
        // RevenueCat / Play Billing wiring lands, gate the button and direct
        // shops to the web for paid plans.
        <div className="border border-white/10 bg-white/5 p-4 text-center" data-testid={`subscribe-gated-${tier}`}>
          <Lock className="w-5 h-5 text-zinc-400 mx-auto mb-2" />
          <div className="text-xs uppercase tracking-widest text-zinc-400 mb-1">Coming soon to mobile</div>
          <div className="text-xs text-zinc-500">
            Subscribe at{" "}
            <a href="https://mechanicar.app/upgrade" target="_blank" rel="noopener noreferrer" className="text-[#FF3B30] underline">
              mechanicar.app
            </a>{" "}
            from any web browser.
          </div>
        </div>
      ) : (
        <>
          <button
            onClick={() => onSubscribeRecurring(trialEligible)}
            disabled={loading || subActive || isCurrent}
            className="btn-primary w-full flex items-center justify-center gap-2"
            style={highlighted ? { background: color } : {}}
            data-testid={trialEligible ? `start-trial-${tier}-btn` : `subscribe-recurring-${tier}-btn`}
          >
            {trialEligible ? <Gift className={`w-4 h-4 ${loading ? "animate-pulse" : ""}`} /> : <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />}
            {loading ? "Opening Stripe…" : ctaLabel}
          </button>

          {trialEligible && !subActive && (
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-2 font-mono text-center" data-testid={`trial-fineprint-${tier}`}>
              {fineprint}
            </div>
          )}

          {subActive && (
            <button
              onClick={onOpenPortal}
              disabled={portalLoading}
              className="btn-secondary w-full mt-3 flex items-center justify-center gap-2"
              data-testid={`manage-subscription-${tier}-btn`}
            >
              <RefreshCw className={`w-4 h-4 ${portalLoading ? "animate-spin" : ""}`} />
              {portalLoading ? "Opening portal…" : "Manage subscription"}
            </button>
          )}
          {subActive && subState?.status && (
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-3 font-mono text-center">
              Stripe · <span className={subState.status === "active" || subState.status === "trialing" ? "text-[#34C759]" : subState.status === "past_due" ? "text-[#FF9500]" : "text-zinc-400"}>{subState.status}</span>
              {subState.cancel_at_period_end ? " · cancels at period end" : ""}
            </div>
          )}
        </>
      )}
    </div>
  );
}
